import { useState, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './debug.module.css';
import { ToastContext } from './util';

/** A titled card; `actions` sit at the right of its title row. */
export function Card({
  title,
  actions,
  flush = false,
  children,
}: {
  title: string;
  actions?: ReactNode;
  flush?: boolean;
  children: ReactNode;
}) {
  return (
    <section className={s.card} aria-label={title}>
      <header className={s.cardHead}>
        <h2>{title}</h2>
        {actions}
      </header>
      <div className={cx(s.cardBody, flush && s.flush)}>{children}</div>
    </section>
  );
}

/** A row of joined choices, one selected (a radio group). */
export function Seg<T extends string | number>({
  label,
  options,
  value,
  onChange,
  format = String,
}: {
  label: string;
  options: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
  format?: (value: T) => string;
}) {
  return (
    <div className={s.seg} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={String(o)}
          type="button"
          role="radio"
          aria-checked={o === value}
          onClick={() => {
            onChange(o);
          }}
        >
          {format(o)}
        </button>
      ))}
    </div>
  );
}

/** Shows short messages at the bottom of the page after an action (`useToast`). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<{ text: string; id: number } | null>(null);
  const show = (text: string) => {
    const id = Date.now();
    setMessage({ text, id });
    setTimeout(() => {
      setMessage((m) => (m?.id === id ? null : m));
    }, 2200);
  };
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div role="status" aria-live="polite">
        {message && <div className={s.toast}>{message.text}</div>}
      </div>
    </ToastContext.Provider>
  );
}
